// src/App.js
import React, { useState, useEffect } from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
  Outlet,
  useParams,
} from "react-router-dom";
import { onAuthStateChanged, signOut, sendEmailVerification } from "firebase/auth";
import { doc, getDoc, updateDoc, serverTimestamp } from "firebase/firestore";
import { auth, db } from "./firebaseConfig";

// --- Import Layout and Context Components ---
import TopNav from "./components/common/TopNav";
import Footer from "./components/common/Footer";
import Sidebar from "./components/common/Sidebar";
import PublicLayout from "./components/landing/PublicLayout";
import { ToastProvider } from "./components/shared/contexts/ToastContext";
import { ConfirmProvider } from "./components/shared/contexts/ConfirmContext";
import ErrorBoundary from "./components/common/ErrorBoundary";

// --- Import Page/Screen Components ---
import LandingPage from "./components/landing/LandingPage";
import HowItWorks from "./components/landing/HowItWorks";
import Login from "./components/auth/Login";
import ResetPassword from "./components/auth/ResetPassword";
import Dashboard from "./components/dashboard/Dashboard";
import ProjectsList from "./components/projects/ProjectsList";
import ProjectOverview from "./components/projects/ProjectOverview";
import Settings from "./components/settings/Settings";
// Notifications handled via dropdown in TopNav — no dedicated page
import Calendar from "./components/calendar/CalendarScreen";
import NewProjectWrapper from "./components/projects/NewProject";
import AiContentHub from "./components/contenthub/AiContentHub";
import EditStream from "./components/streams/EditStream";
import TrendsPage from "./components/trends/TrendsPage";
import Onboarding from "./components/onboarding/Onboarding";
import PrivacyPolicy from "./components/legal/PrivacyPolicy";
import TermsOfService from "./components/legal/TermsOfService";
import CookiePolicy from "./components/legal/CookiePolicy";
import About from "./components/legal/About";
import DataDeletionStatus from "./components/legal/DataDeletionStatus";
import AuthAction from "./components/auth/AuthAction";
import AcceptTeamInvite from "./components/teams/AcceptTeamInvite";

// --- Lazy-loaded routes (code-split for bundle size) ---
const AnalyticsPage = React.lazy(() => import("./components/analytics/AnalyticsPage"));

// --- Main App Component ---
function App() {
  const [user, setUser] = useState(null);
  const [loadingAuth, setLoadingAuth] = useState(true);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (currentUser) => {
      // Login.js drives the Google popup flow itself — don't race it
      if (sessionStorage.getItem("login_in_progress")) {
        return;
      }
      setUser(currentUser);
      if (currentUser) {
        try {
          const userDoc = await getDoc(doc(db, "users", currentUser.uid));
          if (!userDoc.exists()) {
            // Authenticated but no account here — accounts are created by the owner
            await signOut(auth);
            setUser(null);
            setLoadingAuth(false);
            return;
          }
          // Track last active timestamp
          updateDoc(doc(db, "users", currentUser.uid), { lastActiveAt: serverTimestamp() }).catch(() => {});
          // Check for pending team invite in localStorage
          const pendingInvite = localStorage.getItem('pendingTeamInvite');
          if (pendingInvite) {
            localStorage.removeItem('pendingTeamInvite');
            window.location.href = `/team-invite?id=${pendingInvite}`;
          }
        } catch {
          // Non-fatal — auth state is still valid
        }
      }
      setLoadingAuth(false);
    });
    return () => {
      unsubscribe();
    };
  }, []);

  const handleLogout = async () => {
    try {
      await signOut(auth);
    } catch (error) {
      // Error handled silently
    }
  };

  if (loadingAuth) {
    return (
      <div className="flex justify-center items-center min-h-screen bg-warm-dark">
        <p className="text-cream/50 text-sm font-light tracking-widest uppercase">Loading...</p>
      </div>
    );
  }

  // --- Main Router Setup ---
  return (
    <ToastProvider>
      <ConfirmProvider>
      <Router>
        <Routes>
          {/* --- Public Pages with PublicLayout (accessible by everyone) --- */}
          <Route element={<PublicLayout />}>
            <Route index element={!user ? <LandingPage /> : <Navigate to="/dashboard" replace />} />
            <Route path="how-it-works" element={<HowItWorks />} />
            <Route path="about" element={<About />} />
            <Route path="privacy-policy" element={<PrivacyPolicy />} />
            <Route path="terms-of-service" element={<TermsOfService />} />
            <Route path="cookie-policy" element={<CookiePolicy />} />
            <Route path="data-deletion-status" element={<DataDeletionStatus />} />
            <Route path="login" element={!user ? <Login /> : <Navigate to="/dashboard" replace />} />
            <Route path="reset-password" element={!user ? <ResetPassword /> : <Navigate to="/dashboard" replace />} />
          </Route>

          {/* --- Auth utility pages (own layouts) --- */}
          <Route path="auth/action" element={<AuthAction />} />
          <Route path="team-invite" element={<AcceptTeamInvite />} />

          {/* --- Authenticated Routes --- */}
          <Route
            path="/"
            element={
              user ? (
                <AuthenticatedLayout
                  onLogout={handleLogout}
                  userEmail={user?.email}
                  user={user}
                />
              ) : (
                <Navigate to="/login" replace />
              )
            }
          >
            <Route path="dashboard" element={<Dashboard />} />
            <Route path="projects" element={<ProjectsList />} />
            <Route path="create-project" element={<NewProjectWrapper />} />
            <Route path="project/:projectId" element={<ProjectOverview />} />
            <Route path="content-hub" element={<AiContentHub />} />
            <Route
              path="project/:projectId/content-hub"
              element={<AiContentHub />}
            />
            <Route path="trends" element={<TrendsPage />} />
            <Route path="analytics" element={<React.Suspense fallback={<div className="flex items-center justify-center py-24"><div className="w-6 h-6 border-2 border-slate-blue border-t-transparent rounded-full animate-spin" /></div>}><AnalyticsPage /></React.Suspense>} />
            <Route
              path="project/:projectId/analytics"
              element={<AnalyticsRedirect />}
            />
            <Route
              path="project/:projectId/stream/:streamId"
              element={<EditStream />}
            />
            <Route
              path="settings"
              element={<Settings onLogout={handleLogout} />}
            />
            <Route path="calendar" element={<Calendar />} />
            <Route path="help" element={<Onboarding />} />
          </Route>

          {/* --- Catch-all Route --- */}
          <Route
            path="*"
            element={<Navigate to={user ? "/dashboard" : "/"} replace />}
          />
        </Routes>
      </Router>
      </ConfirmProvider>
    </ToastProvider>
  );
}

// --- Redirect helper for old analytics URL ---
function AnalyticsRedirect() {
  const { projectId } = useParams();
  return <Navigate to={`/analytics?project=${projectId}`} replace />;
}

// --- Layout Components ---

function EmailVerificationBanner({ user }) {
  const [dismissed, setDismissed] = useState(false);
  const [resending, setResending] = useState(false);
  const [cooldown, setCooldown] = useState(false);

  // Only show for email+password users who haven't verified
  const isPasswordUser = user?.providerData?.[0]?.providerId === "password";
  if (!user || user.emailVerified || !isPasswordUser || dismissed) return null;

  const handleResend = async () => {
    setResending(true);
    try {
      await sendEmailVerification(user);
      setCooldown(true);
      setTimeout(() => setCooldown(false), 60000);
    } catch {
      // silently fail
    } finally {
      setResending(false);
    }
  };

  return (
    <div className="bg-yellow-500/10 border-b border-yellow-500/20 px-4 py-2.5 flex items-center justify-center gap-3 text-xs">
      <span className="text-yellow-400 font-medium">
        Please verify your email address. Check your inbox for a verification link.
      </span>
      <button
        type="button"
        onClick={handleResend}
        disabled={resending || cooldown}
        className="px-3 py-1 bg-yellow-500/20 text-yellow-400 rounded-lg font-bold uppercase tracking-wider hover:bg-yellow-500/30 transition-colors disabled:opacity-40 text-[10px]"
      >
        {cooldown ? "Sent!" : resending ? "..." : "Resend"}
      </button>
      <button
        type="button"
        onClick={() => setDismissed(true)}
        className="text-yellow-400/50 hover:text-yellow-400 transition-colors ml-1"
        aria-label="Dismiss"
      >
        &times;
      </button>
    </div>
  );
}

function AuthenticatedLayout({ onLogout, userEmail, user }) {
  return (
    <div className="flex h-screen bg-warm-dark">
      <Sidebar />
      <div className="flex flex-col flex-1">
        <TopNav userEmail={userEmail} onLogout={onLogout} />
        <EmailVerificationBanner user={user} />
        <main className="flex-1 overflow-y-auto pt-24">
          <ErrorBoundary>
            <Outlet />
          </ErrorBoundary>
        </main>
        <Footer variant="minimal" />
      </div>
    </div>
  );
}

export default App;
